import { useNavigate } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { useWorkspaceService } from '../../../_services/WorkspaceService'
import { useAppsService } from '../../../_services/AppsService'
import { useFetch } from '../../../_hooks/useFetch'
import _endpoints from '../../../_endpoints'
import { setLaunchingWorkspace } from '../../../_redux/actions/workspaceActions'
import { getLayoutModelForVariant, workspaceSchema } from './constants'
import {
  SERVER_TO_LAYOUT_VARIANT,
  WORKSPACE_ITEM_TYPE,
  WorkspaceDetail
} from './modals/createWorkspacePayload'
import type { WorkspacePrivacy } from './modals/CreateWorkspaceModal'

export interface LaunchableWorkspace {
  id: string | number
  name: string
}

/**
 * Shared "launch a server workspace onto the desktop" flow, reused by the My
 * space / Teams workspace lists and the workspace tab-strip "+" flyout.
 *
 * For a snappy page transition the workspace is shown immediately
 * (optimistically) and then the saved layout + apps are fetched in the
 * background and merged in. The server copy is the source of truth (edits are
 * persisted via PUT, not to the local redux copy).
 */
export const useWorkspaceLauncher = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const workspaceList =
    useSelector((state: any) => state?.workspaceReducer?.workspaces?.workspaceList) || []
  const { getWorkspaceById, addWorkspaceToList, updateActiveWorkspace, updateWorkspaceList } =
    useWorkspaceService()
  const { getApplication } = useAppsService()
  const [, getApps] = useFetch()

  const launchWorkspace = async (
    row: LaunchableWorkspace,
    privacyHint: WorkspacePrivacy = 'personal'
  ) => {
    const derivedId = `ws-server-${row.id}`
    // The workspace may already be on the desktop either because it was launched
    // before (id === derivedId) or because it was just created this session (a
    // random client id linked to the same server workspace via serverWorkspaceId).
    const existing = workspaceList.find(
      (ws: any) => ws?.id === derivedId || String(ws?.serverWorkspaceId ?? '') === String(row.id)
    )
    const targetId = existing?.id ?? derivedId

    // 1) Show the workspace right away so we leave the current page instantly,
    // and flag it as launching so the desktop shows a loader until apps are ready.
    dispatch(setLaunchingWorkspace(targetId))
    if (existing) {
      updateActiveWorkspace(targetId)
    } else {
      addWorkspaceToList({
        ...workspaceSchema,
        id: derivedId,
        serverWorkspaceId: row.id,
        name: row.name,
        model: getLayoutModelForVariant([], 'single'),
        layout: { variant: 'single', aliPanel: false },
        // Categorises the workspace in the left-nav submenu; refined from the
        // server detail once it loads below.
        privacy: privacyHint ?? 'personal'
      })
    }
    navigate('/workspace')

    try {
      // 2) Fetch the saved workspace and its apps, then fill it in.
      const detail: WorkspaceDetail | null = await getWorkspaceById(row.id)
      if (!detail) return

      const appItems = (detail.items || [])
        .filter(
          (item: any) =>
            item?.itemType === WORKSPACE_ITEM_TYPE.APPLICATION && item?.applicationId !== null
        )
        .sort((a: any, b: any) => (a.position ?? 0) - (b.position ?? 0))

      // Server items only carry a numeric applicationId, but getApplication (and
      // the iframe url it returns) is keyed by uniqueName. Resolve the catalogue
      // once to map applicationId -> uniqueName, then fetch each full app.
      const catalogueResp = await getApps(
        _endpoints.search.get.concat(_endpoints.search.newArrivalsQuery)
      )
      const catalogue: any[] = catalogueResp?.applications || []
      const catalogueByAppId = new Map<string, any>()
      catalogue.forEach((app: any) => {
        if (app?.applicationId !== null) catalogueByAppId.set(String(app.applicationId), app)
        if (app?.id !== null) catalogueByAppId.set(String(app.id), app)
      })

      const apps = (
        await Promise.all(
          appItems.map(async (item: any) => {
            const catalogueApp = catalogueByAppId.get(String(item.applicationId))
            const uniqueName = catalogueApp?.uniqueName
            if (uniqueName) {
              const full = (await getApplication(uniqueName))?.data
              if (full) return full
            }
            return catalogueApp || null
          })
        )
      ).filter(Boolean)

      const variant = SERVER_TO_LAYOUT_VARIANT[detail.layout] || 'single'
      // The API returns the owning team GUID as `teamExternalId`; keep `teamId`
      // as a fallback for any older/aliased responses.
      const detailTeamId = detail.teamExternalId ?? detail.teamId
      // Merge the resolved layout + apps into the workspace already on the desktop.
      // The workspaceList change re-runs the Ali-panel effect, opening it when the
      // saved workspace has includeAliPanel set.
      updateWorkspaceList(
        {
          ...workspaceSchema,
          id: targetId,
          serverWorkspaceId: row.id,
          name: detail.name,
          model: getLayoutModelForVariant(apps, variant),
          layout: { variant, aliPanel: Boolean(detail.includeAliPanel) },
          privacy: detail.privacy === 'Team' ? 'team' : 'personal',
          ...(detailTeamId !== null ? { teamId: detailTeamId } : {})
        },
        targetId
      )
    } finally {
      dispatch(setLaunchingWorkspace(null))
    }
  }

  return { launchWorkspace }
}
